import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { route } from 'preact-router';

@observer
class RelatedPosts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
        };
    }

    componentDidMount() {
        this.loadRelated();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.articleId !== this.props.articleId) {
            this.loadRelated();
        }
    }

    componentWillUnmount() {
        const { articleStore } = this.props.stores;
        articleStore.clearRelatedArticles();
    }

    loadRelated = async () => {
        const { articleId, stores, hostname, limit = 5 } = this.props;
        const { articleStore } = stores;

        if (!articleId) {
            return;
        }

        this.setState({ loading: true });
        await articleStore.loadRelatedArticles(articleId, {
            hostname: hostname || articleStore.currentHostname,
            limit,
        });
        this.setState({ loading: false });
    }

    onClickArticle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { uuidv4, title } = e.currentTarget.dataset;
        const { onArticleClick } = this.props;

        if (onArticleClick) {
            onArticleClick({ uuidv4, title });
        } else {
            // Default navigation
            route(`/article/${encodeURIComponent(title).toLocaleLowerCase()}/${uuidv4}`);
        }
    }

    render() {
        const { loading } = this.state;
        const { stores, darkmode, classNames = '' } = this.props;
        const { articleStore, userStore } = stores;
        const { relatedArticles = [] } = articleStore;
        const { user = {} } = userStore;
        const isDarkmode = darkmode !== undefined ? darkmode : util.getNestedValue(user, 'settings.darkmode');
        const tagClass = isDarkmode ? 'dark' : 'light';

        if (loading) {
            return (
                <div class={`d-flex flex-column w-100 px-3 py-3 ${classNames}`}>
                    <h6 class='text-muted mb-3'>
                        <i class='fa-solid fa-sparkles me-2' />
                        <Text id='article.related-posts'>Related posts</Text>
                    </h6>
                    <div class='d-flex justify-content-center'>
                        <div class='spinner-border spinner-border-sm text-muted' role='status'>
                            <span class='visually-hidden'>Loading...</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (relatedArticles.length === 0) {
            return null;
        }

        return (
            <div class={`d-flex flex-column w-100 px-3 py-3 ${classNames}`}>
                <h6 class='text-muted mb-3'>
                    <i class='fa-solid fa-sparkles me-2' />
                    <Text id='article.related-posts'>Related posts</Text>
                </h6>
                <div class='d-flex flex-column gap-2'>
                    {relatedArticles.map(article => (
                        <div
                            key={article.id}
                            class={`d-flex flex-row align-items-start p-2 rounded ${isDarkmode ? 'bg-dark' : 'bg-light'}`}
                            style='cursor: pointer;'
                            onClick={this.onClickArticle}
                            data-uuidv4={article.uuidv4}
                            data-title={article.title}
                        >
                            {/* Thumbnail */}
                            {article.images?.[0]?.s3SmallLink ? (
                                <img
                                    src={article.images[0].s3SmallLink}
                                    alt={article.images[0].alt || article.title}
                                    class='rounded me-2'
                                    style='width: 60px; height: 60px; object-fit: cover;'
                                />
                            ) : (
                                <div
                                    class={`rounded me-2 d-flex align-items-center justify-content-center ${isDarkmode ? 'bg-secondary' : 'bg-white'}`}
                                    style='width: 60px; height: 60px;'
                                >
                                    <i class='fa-solid fa-image text-muted' />
                                </div>
                            )}

                            {/* Content */}
                            <div class='d-flex flex-column flex-fill overflow-hidden'>
                                <span
                                    class={`fw-semibold ${isDarkmode ? 'text-light' : 'text-dark'}`}
                                    style='font-size: 0.9rem; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;'
                                >
                                    {article.title}
                                </span>

                                {/* Matched tags */}
                                {article.matchedTags?.length > 0 && (
                                    <div class='d-flex flex-wrap gap-1 mt-1'>
                                        {article.matchedTags.slice(0, 3).map(tag => (
                                            <span
                                                key={tag}
                                                class={`badge text-bg-${tagClass}`}
                                                style='font-size: 0.65rem;'
                                            >
                                                #{tag}
                                            </span>
                                        ))}
                                        {article.matchedTags.length > 3 && (
                                            <span class='badge text-bg-secondary' style='font-size: 0.65rem;'>
                                                +{article.matchedTags.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default RelatedPosts;
